import { motion } from 'framer-motion';
import MarketingLayout from '../components/MarketingLayout';
import SEO from '../components/SEO';

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

export default function CookiePolicyPage() {
  return (
    <MarketingLayout>
      <SEO
        title="Cookie Policy"
        description="PAFHub Cookie Policy — how we use cookies and similar technologies on our platform."
        path="/cookies"
      />
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 bg-gradient-to-br from-indigo-50/80 via-white to-sky-50/50">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-3">Legal</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight mb-4">Cookie Policy</h1>
            <p className="text-[#64748B]">Last updated: April 1, 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 prose prose-slate prose-headings:text-[#0F172A] prose-headings:font-bold prose-p:text-[#475569] prose-p:leading-[1.8] prose-li:text-[#475569] prose-strong:text-[#0F172A] max-w-none">

          <h2>1. What Are Cookies?</h2>
          <p>Cookies are small text files that are placed on your device when you visit a website. They are widely used to make websites work more efficiently, provide a better user experience, and give website operators useful information. PAFHub uses cookies and similar technologies to operate our platform securely and effectively.</p>

          <h2>2. How PAFHub Uses Cookies</h2>
          <p>We use the following categories of cookies:</p>

          <h3>2.1 Essential Cookies (Required)</h3>
          <p>These cookies are necessary for the platform to function and cannot be disabled. They include:</p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 pr-4 font-semibold text-[#0F172A]">Cookie</th>
                  <th className="text-left py-3 pr-4 font-semibold text-[#0F172A]">Purpose</th>
                  <th className="text-left py-3 font-semibold text-[#0F172A]">Duration</th>
                </tr>
              </thead>
              <tbody className="text-[#475569]">
                <tr className="border-b border-slate-100">
                  <td className="py-3 pr-4 font-mono text-xs">session_token</td>
                  <td className="py-3 pr-4">Authenticates your session when logged in via Google OAuth</td>
                  <td className="py-3">7 days</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-3 pr-4 font-mono text-xs">paf_token (localStorage)</td>
                  <td className="py-3 pr-4">Stores your JWT authentication token for email/password login</td>
                  <td className="py-3">7 days</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>2.2 Analytics Cookies</h3>
          <p>We use PostHog for product analytics to understand how users interact with our platform. This helps us improve features and fix issues. PostHog may set the following cookies:</p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 pr-4 font-semibold text-[#0F172A]">Cookie</th>
                  <th className="text-left py-3 pr-4 font-semibold text-[#0F172A]">Purpose</th>
                  <th className="text-left py-3 font-semibold text-[#0F172A]">Duration</th>
                </tr>
              </thead>
              <tbody className="text-[#475569]">
                <tr className="border-b border-slate-100">
                  <td className="py-3 pr-4 font-mono text-xs">ph_*</td>
                  <td className="py-3 pr-4">PostHog analytics — tracks anonymous usage patterns, page views, and feature adoption</td>
                  <td className="py-3">1 year</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>2.3 Third-Party Cookies</h3>
          <p>When you interact with third-party services through PAFHub, those services may set their own cookies:</p>
          <ul>
            <li><strong>Stripe:</strong> When processing payments, Stripe may set cookies for fraud prevention and payment processing</li>
            <li><strong>Google OAuth:</strong> When signing in with Google, Google's authentication service may set cookies</li>
          </ul>

          <h2>3. Local Storage</h2>
          <p>In addition to cookies, PAFHub uses your browser's local storage to store your authentication token (JWT). This allows you to remain logged in between browser sessions. Clearing your browser's local storage will log you out of PAFHub.</p>

          <h2>4. Managing Cookies</h2>
          <p>You can manage cookies through your browser settings. Most browsers allow you to:</p>
          <ul>
            <li>View what cookies are set on your device</li>
            <li>Delete individual or all cookies</li>
            <li>Block cookies from specific or all websites</li>
            <li>Set preferences for first-party vs. third-party cookies</li>
          </ul>
          <p><strong>Please note:</strong> Disabling essential cookies will prevent you from logging into and using PAFHub. The platform requires authentication cookies to function.</p>

          <h3>Browser-Specific Instructions</h3>
          <ul>
            <li><strong>Chrome:</strong> Settings &gt; Privacy and Security &gt; Cookies and other site data</li>
            <li><strong>Firefox:</strong> Settings &gt; Privacy & Security &gt; Cookies and Site Data</li>
            <li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Manage Website Data</li>
            <li><strong>Edge:</strong> Settings &gt; Cookies and site permissions &gt; Cookies and site data</li>
          </ul>

          <h2>5. Do Not Track</h2>
          <p>Some browsers offer a "Do Not Track" (DNT) setting. PAFHub currently does not respond to DNT signals, as there is no industry standard for how to interpret them. We encourage you to review this policy and manage your cookie preferences using the browser controls described above.</p>

          <h2>6. Changes to This Policy</h2>
          <p>We may update this Cookie Policy from time to time to reflect changes in technology, regulation, or our business practices. We will post the updated policy on this page and update the "Last updated" date.</p>

          <h2>7. Contact Us</h2>
          <p>If you have questions about our use of cookies, please contact us at:</p>
          <ul>
            <li>Email: compliance@pafhub.com</li>
            <li>Website: pafhub.com/contact</li>
          </ul>
        </div>
      </section>
    </MarketingLayout>
  );
}
